"use client";

import { Shield, Star, Users, Award, CheckCircle, TrendingUp, Truck, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import type { CarouselApi } from "@/components/ui/carousel";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

interface TrustIndicator {
  id: string;
  icon: any;
  text: string;
  subtext: string;
  color: string;
}

const trustIndicators: TrustIndicator[] = [
  {
    id: "properties",
    icon: Users,
    text: "10,000+ Properties",
    subtext: "Trusted Nationwide",
    color: "from-blue-500 to-indigo-600"
  },
  {
    id: "support",
    icon: CheckCircle,
    text: "24/7 Support",
    subtext: "Always Available",
    color: "from-emerald-500 to-green-600"
  },
  {
    id: "experience",
    icon: TrendingUp,
    text: "10+ Years",
    subtext: "Industry Experience",
    color: "from-indigo-500 to-blue-600"
  },
  {
    id: "excellence",
    icon: Award,
    text: "Industry Leading",
    subtext: "Market Excellence",
    color: "from-purple-500 to-violet-600"
  },
  {
    id: "logistics",
    icon: Truck,
    text: "Own Logistics",
    subtext: "Full Network Control",
    color: "from-cyan-500 to-blue-600"
  },
  {
    id: "training",
    icon: Shield,
    text: "Training Resources",
    subtext: "Expert Development",
    color: "from-rose-500 to-pink-600"
  },
  {
    id: "quality",
    icon: Star,
    text: "Quality Standards",
    subtext: "Consistent Excellence",
    color: "from-amber-500 to-orange-600"
  },
  {
    id: "response",
    icon: Clock,
    text: "Rapid Response",
    subtext: "Quick Deployment",
    color: "from-teal-500 to-cyan-600"
  }
];

interface TrustIndicatorsGalleryProps {
  className?: string;
}

export function TrustIndicatorsGallery({ className }: TrustIndicatorsGalleryProps) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  // Auto-scroll every 4 seconds
  useEffect(() => {
    if (!carouselApi) {
      return;
    }
    
    const intervalId = setInterval(() => {
      carouselApi.scrollNext();
    }, 4000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [carouselApi]);

  return (
    <section className={cn("w-full", className)}>
      {/* Carousel */}
      <div className="w-full max-w-full">
        <Carousel
          setApi={setCarouselApi}
          opts={{
            align: "start",
            loop: true,
            containScroll: "trimSnaps",
          }}
          className="relative w-full max-w-full"
        >
          <CarouselContent className="-ml-4 lg:-ml-6">
            {trustIndicators.map((item) => (
              <CarouselItem 
                key={item.id} 
                className="pl-4 lg:pl-6 basis-[85%] sm:basis-[65%] md:basis-[45%] lg:basis-1/4"
              >
                <div className="group cursor-pointer py-2 select-none">
                  {/* Compact horizontal card */}
                  <div className="flex items-center gap-4 sm:gap-5 rounded-2xl p-4 sm:p-5 transition-all duration-300">
                    {/* Icon with gradient background */}
                    <div className={`flex-shrink-0 inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-r ${item.color} rounded-xl`}>
                      <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                    </div>
                    
                    {/* Text content */}
                    <div className="flex flex-col min-w-0">
                      <div className="text-base sm:text-lg font-satoshi font-semibold text-gray-800 leading-tight truncate">
                        {item.text}
                      </div>
                      <div className="text-base sm:text-lg font-light text-gray-600 leading-relaxed truncate">
                        {item.subtext}
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  );
}
